 
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import Image from 'next/image'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { useCallback, useEffect, useState } from 'react'

import Event from '@/domains/event'
import { useMarkdown } from '@/lib/markdown'
import { currentUser } from '@/lib/session'
import {
  capitalizeFirstLetter,
  getLocation,
  numberWithCommas,
  simpleLocalDateFormat
} from '@/lib/utils'
import { useAuth } from '@/contexts/auth'

dayjs.extend(utc)
dayjs.extend(timezone)

// events/[:id]
export default function Show({ event }) {
  const { isReady } = useRouter()
  const { user: currentUser, accessToken } = useAuth()
  const { t } = useTranslation()
  const router = useRouter()
  const currentDetails = useMarkdown(event.details)
  const currentShirtsDetails = useMarkdown(event.shirtsDetails)
  const currentPrizesDetails = useMarkdown(event.prizesDetails)
  const currentMap = useMarkdown(event.map)
  const currentEquipments = useMarkdown(event.equipments)
  const currentSchedule = useMarkdown(event.schedule)
  const currentOther = useMarkdown(event.other)
  const currentContactUs = useMarkdown(event.contactUs)
  const agreement = useMarkdown(event.agreement)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [hasShirtLength, setHasShirtLength] = useState(false)
  const [allowRegister, setAllowRegister] = useState(true)
  const { preferences } = event
  const { titles } = preferences
  const today = new Date()

  const getCurrentRate = () => {
    if (
      event.loyaltyEndAt &&
      today <
        dayjs(event.loyaltyEndAt)
          .tz('Asia/Bangkok')
          .subtract(7, 'hours')
          .toDate()
    ) {
      return 'loyalty'
    }

    if (
      event.superEarlyBirdEndAt &&
      today <
        dayjs(event.superEarlyBirdEndAt)
          .tz('Asia/Bangkok')
          .subtract(7, 'hours')
          .toDate()
    ) {
      return 'superEarlyBird'
    }

    if (
      event.earlyBirdEndAt &&
      today <
        dayjs(event.earlyBirdEndAt)
          .tz('Asia/Bangkok')
          .subtract(7, 'hours')
          .toDate()
    ) {
      return 'earlyBird'
    }

    return 'normal'
  }

  const { report } = event
  const [isAllMax, setIsAllMax] = useState(false)

  const getCurrentPrice = (category) => {
    const hasLoyalty = !!event.loyaltyEndAt
    const hasSuperEarlyBird = !!event.superEarlyBirdEndAt

    if (getCurrentRate() === 'loyalty') {
      return {
        name: 'Loyalty',
        price: category.loyaltyRate,
        startAt: event.registrationStartAt,
        endAt: event.loyaltyEndAt,
        type: 'loyalty'
      }
    }

    if (getCurrentRate() === 'superEarlyBird') {
      return {
        name: 'Super Early Bird',
        price: category.superEarlyBirdRate,
        startAt: hasLoyalty ? event.loyaltyEndAt : event.registrationStartAt,
        endAt: event.superEarlyBirdEndAt,
        type: 'superEarlyBird'
      }
    }

    if (getCurrentRate() === 'earlyBird') {
      return {
        name: 'Early Bird',
        price: category.earlyBirdRate,
        startAt: hasSuperEarlyBird
          ? event.superEarlyBirdEndAt
          : event.registrationStartAt,
        endAt: event.earlyBirdEndAt,
        type: 'earlyBird'
      }
    }

    return { name: '', price: category.rate, type: 'normal' }
  }

  const categoriesList = event.categories.map((category) => {
    const currentReport = report.categories.find(
      (v) => v.name === category.name
    )
    const currentPrice = getCurrentPrice(category)
    const isMax =
      currentReport[`isMax${capitalizeFirstLetter(getCurrentRate())}`]
    const suffixLabel =
      currentPrice.name !== '' ? ` (${currentPrice.name})` : ''
    const duration = currentPrice.startAt ? (
      <span className="pl-0 text-red-400 md:pl-4">
        ({simpleLocalDateFormat(dayjs(currentPrice.startAt)
        .tz('Asia/Bangkok')
        .subtract(7, 'hours')
        .toDate())} -{' '}
        {simpleLocalDateFormat(dayjs(currentPrice.endAt)
          .tz('Asia/Bangkok')
          .subtract(7, 'hours')
          .toDate())})
      </span>
    ) : (
      <></>
    )
    return (
      <div
        key={`category_${category.name}`}
        className="flex flex-col border border-gray-500 md:flex-row"
      >
        <div className="flex w-full items-center bg-primary p-2 text-sm text-white md:w-1/2 md:text-base">
          {category.name}
        </div>

        <div className="flex w-full items-center justify-between p-2 text-sm md:w-1/2 md:text-base">
          <div className="flex w-full justify-between gap-4">
            <p className="flex flex-row items-center justify-center gap-2">
              {`${numberWithCommas(currentPrice.price)} บาท`}
              {isMax && (
                <span className="badge bg-red-400 p-3 text-white">เต็ม</span>
              )}
            </p>
            <div className="md:tex-left flex w-fit flex-col items-center text-right md:flex-row md:justify-end">
              {suffixLabel}
              {duration}
            </div>
          </div>
        </div>
      </div>
    )
  })

  const shirtSize = event.shirts.map((shirt, i) => {
    return (
      <tr key={i}>
        <td>{shirt.name}</td>
        <td>{shirt.chest}</td>
        {shirt.length && shirt.length !== '' && <td>{shirt.length}</td>}
      </tr>
    )
  })

  function handleOnRegister() {
    router.push(`/events/${event.slug}/register`)
  }

  const fetchStatus = useCallback(async (accessToken) => {
    const result = await new Event().status(event.id, accessToken)
    if (result.status === 'submitted') {
      setHasSubmitted(true)
    }
  }, [])

  useEffect(() => {
    const today = new Date()
    if (today > dayjs(event.registrationEndAt)
      .tz('Asia/Bangkok')
      .subtract(7, 'hours')
      .toDate()) {
      setAllowRegister(false)
    }

    if (
      event.shirts?.length > 0 &&
      Object.keys(event.shirts[0]).length === 3 &&
      event.shirts[0].length !== ''
    ) {
      setHasShirtLength(true)
    }

    const _isAllMax = event.categories
      .map((category) => {
        const currentReport = report.categories.find(
          (v) => v.name === category.name
        )
        return currentReport[`isMax${capitalizeFirstLetter(getCurrentRate())}`]
      })
      .reduce((prev, current) => prev && current, true)
    setIsAllMax(_isAllMax)
    if (currentUser) {
      fetchStatus(accessToken())
    }
  }, [currentUser])

  return (
    <div className="mx-auto w-full px-2 2xl:px-0">
      <main className="relative">
        <div className="sticky top-0 z-30 flex items-center justify-end gap-2 bg-white py-2">
          {currentUser ? (
            allowRegister ? (
              !isAllMax ? (
                <button
                  className="btn btn-primary h-16 px-10 text-lg text-white md:text-3xl"
                  onClick={handleOnRegister}
                >
                  {t('race.registration')}
                </button>
              ) : (
                <span className="badge h-16 rounded-xl bg-red-400 px-10 text-lg text-white md:text-3xl">
                  {t('race.full')}
                </span>
              )
            ) : (
              <span className="badge h-16 rounded-xl bg-red-400 px-10 text-lg text-white md:text-3xl">
                {t('race.overdue')}
              </span>
            )
          ) : (
            <span className="badge h-16 rounded-xl bg-red-400 px-10 text-lg text-white md:text-3xl">
              {t('please_login')}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-y-2">
          <span>
            {t('organizer')} : {event.organizer.organizerName}
          </span>
          <h1 className="text-xl font-bold leading-normal md:text-3xl">
            {event.name}
          </h1>
          <div className="flex gap-x-4">
            <span className="flex gap-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5m-9-6h.008v.008H12v-.008zM12 15h.008v.008H12V15zm0 2.25h.008v.008H12v-.008zM9.75 15h.008v.008H9.75V15zm0 2.25h.008v.008H9.75v-.008zM7.5 15h.008v.008H7.5V15zm0 2.25h.008v.008H7.5v-.008zm6.75-4.5h.008v.008h-.008v-.008zm0 2.25h.008v.008h-.008V15zm0 2.25h.008v.008h-.008v-.008zm2.25-4.5h.008v.008H16.5v-.008zm0 2.25h.008v.008H16.5V15z"
                />
              </svg>
              {simpleLocalDateFormat(new Date(event.eventStartAt))}
            </span>
            <span className="flex gap-x-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="h-6 w-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z"
                />
              </svg>
              {getLocation(event)}
            </span>
          </div>

          <div>
            <Image
              alt={event.name}
              className="h-auto w-full object-cover"
              src={event.coverUrl}
              width={800}
              height={400}
            />
          </div>
        </div>

        {currentDetails && (
          <section id="general" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.general || t('race.general')}
            </h2>
            <div className="article">{currentDetails}</div>
          </section>
        )}

        <section id="categories" className="mt-4 flex flex-col gap-4">
          <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
            {titles.raceType || 'ประเภท'}
          </h2>
          {categoriesList}
        </section>

        {currentSchedule && (
          <section id="schedule" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.schedule || 'กำหนดการ'}
            </h2>
            <div className="article">{currentSchedule}</div>
          </section>
        )}

        <section className="mt-4 flex flex-col gap-4">
          <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
            {titles.shirt || 'เสื้อ'}
          </h2>
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th className="">Size</th>
                  <th className="">รอบอก (นิ้ว)</th>
                  {hasShirtLength && <th>ความยาว (นิ้ว)</th>}
                </tr>
              </thead>
              <tbody>{shirtSize}</tbody>
            </table>
          </div>
        </section>

        {currentShirtsDetails && (
          <section id="shirtsDetails" className="mt-4 flex flex-col gap-4">
            <div className="article">{currentShirtsDetails}</div>
          </section>
        )}

        {currentPrizesDetails && (
          <section id="prizes" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.medal || 'เหรียญ/รางวัล'}
            </h2>
            <div className="article">{currentPrizesDetails}</div>
          </section>
        )}

        {currentMap && (
          <section id="schedule" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.map || 'แผนที่'}
            </h2>
            <div className="article">{currentMap}</div>
          </section>
        )}

        {currentEquipments && (
          <section id="equipments" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.equipments || 'รับอุปกรณ์'}
            </h2>
            <div className="article">{currentEquipments}</div>
          </section>
        )}

        {agreement && (
          <section id="equipments" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.agreement || 'กติกา'}
            </h2>
            <div className="article">{agreement}</div>
          </section>
        )}

        {currentOther && (
          <section id="other" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {titles.others || 'อื่นๆ'}
            </h2>
            <div className="article">{currentOther}</div>
          </section>
        )}

        {currentContactUs && (
          <section id="contactUs" className="mt-4 flex flex-col gap-4">
            <h2 className="text-normal bg-gray-300 py-2 text-center text-xl font-bold md:py-4">
              {t('race.contact_us')}
            </h2>
            <div className="article">{currentContactUs}</div>
          </section>
        )}
      </main>
    </div>
  )
}

export async function getServerSideProps(context) {
  const { slug } = context.query
  const user = await currentUser(context.req)
  const event = await new Event().showBySlug(slug)
  return {
    props: {
      user,
      event,
      ...(await serverSideTranslations(context.locale, ['common']))
    }
  }
}
